import { Component, ElementRef, OnDestroy, OnInit, ViewChild, DestroyRef, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

import { MobileDockService } from 'src/app/_services/mobile-dock.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trackByValue } from '../utils/track-by';
import { OUTCOMES_ROUTES } from '../utils/nav-active';

interface IQuickLink {
  route: string;
  label: string;
  icon: string;
  /** Read out in place of the label, which is shortened to fit four across a phone. */
  description: string;
  /**
   * Every route this link stands for. Outcomes spans two of them, so the highlight
   * is matched here rather than left to routerLinkActive, which only knows about
   * the link's own URL.
   */
  routes: ReadonlyArray<string>;
}

/**
 * Quick links to the four tools, docked at the foot of the viewport on phones and
 * tablets. They fill the slot on every page that does not dock the period and area
 * selector, so the bottom of the screen always offers either where you are or where
 * to go next. The destinations are the four cards on the landing page.
 */
@Component({
    selector: 'app-mobile-nav',
    templateUrl: './mobile-nav.component.html',
    styleUrls: ['./mobile-nav.component.scss'],
    standalone: false
})
export class MobileNavComponent implements OnInit, OnDestroy {
  public readonly trackByValue = trackByValue;

  // Ties every subscription below to this component's lifetime, so a response
  // that arrives after the user has navigated away is dropped instead of
  // writing into a view that no longer exists.
  private readonly destroyRef = inject(DestroyRef);


  public readonly links: Array<IQuickLink> = [
    { route: '/insights', label: 'Insights', icon: 'far fa-file-chart-line', description: 'Insights', routes: ['/insights'] },
    { route: '/outcomes-intro', label: 'Outcomes', icon: 'far fa-shield-check', description: 'Outcomes', routes: OUTCOMES_ROUTES }
  ];

  public visible: boolean = false;
  /** The current path, without its query string, for the highlight above. */
  public activePath: string = '';

  private readonly subscriptions = new Subscription();
  private observer: ResizeObserver;

  /** The bar comes and goes with the route, so it is measured whenever it appears. */
  @ViewChild('bar') public set bar(element: ElementRef<HTMLElement>) {
    this.observer?.disconnect();
    if (!element) {
      return;
    }
    this.observer = new ResizeObserver(() => {
      this.dock.setHeight(element.nativeElement.getBoundingClientRect().height);
    });
    this.observer.observe(element.nativeElement);
  }

  constructor(private dock: MobileDockService, private router: Router) { }

  public isActive(link: IQuickLink): boolean {
    return link.routes.includes(this.activePath);
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.router.url),
        // NavigationEnd has usually already fired by the time the bar is created,
        // so the current URL seeds the highlight.
        startWith(this.router.url),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(url => {
        this.activePath = url.split('?')[0].split('#')[0];
      })
    );

    this.subscriptions.add(
      combineLatest([this.dock.compact$, this.dock.selectorDocked$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([compact, selectorDocked]) => {
        this.visible = compact && !selectorDocked;
        if (!this.visible) {
          this.observer?.disconnect();
          // Left alone when the selector has the slot, since it publishes its own
          // height. Otherwise nothing is docked and the slot is empty.
          if (!selectorDocked) {
            this.dock.setHeight(null);
          }
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.observer?.disconnect();
  }
}
